import React from 'react';
import i18n from 'util/i18n';
import { Summary, DateSummary } from 'components/Summary';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalPoliceOtherOffenseItemModel } from 'forms/sections/legal/police/otherOffense/types';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import Accordion from 'components/Form/Accordion';
import Show from 'components/Form/Show';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation/Location';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import Charge from './Charge';
import Sentence from './Sentence';

interface Props extends Partial<LegalPoliceOtherOffenseItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  defaultState?: boolean
  requireLegalPoliceDrugs?: boolean
  requireLegalPoliceFirearms?: boolean
  errors?: any
}

export default class OtherOffense extends ValidationElement<Props> {
  update = (queue: Partial<LegalPoliceOtherOffenseItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date,
      OffenseDate: this.props.OffenseDate,
      OffenseAddress: this.props.OffenseAddress,
      Description: this.props.Description,
      InvolvedViolence: this.props.InvolvedViolence,
      InvolvedFirearms: this.props.InvolvedFirearms,
      InvolvedExplosives: this.props.InvolvedExplosives,
      InvolvedSubstances: this.props.InvolvedSubstances,
      InvolvedAlcohol: this.props.InvolvedAlcohol,
      ReasonNotCharged: this.props.ReasonNotCharged,
      CourtName: this.props.CourtName,
      CourtAddress: this.props.CourtAddress,
      Charges: this.props.Charges,
      WasSentenced: this.props.WasSentenced,
      Sentence: this.props.Sentence,
      AwaitingTrial: this.props.AwaitingTrial,
      AwaitingTrialExplanation: this.props.AwaitingTrialExplanation,
      ...queue,
    });
  };

  updateDate = (values: LegalPoliceOtherOffenseItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateDescription = (values: LegalPoliceOtherOffenseItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateOffenseDate = (values: LegalPoliceOtherOffenseItemModel['OffenseDate']) => {
    this.update({
      OffenseDate: values,
    });
  };

  updateOffenseAddress = (values: LegalPoliceOtherOffenseItemModel['OffenseAddress']) => {
    this.update({
      OffenseAddress: values,
    });
  };

  updateInvolvedViolence = (values: LegalPoliceOtherOffenseItemModel['InvolvedViolence']) => {
    this.update({
      InvolvedViolence: values,
    });
  };

  updateInvolvedFirearms = (values: LegalPoliceOtherOffenseItemModel['InvolvedFirearms']) => {
    this.update({
      InvolvedFirearms: values,
    });
  };

  updateInvolvedExplosives = (values: LegalPoliceOtherOffenseItemModel['InvolvedExplosives']) => {
    this.update({
      InvolvedExplosives: values,
    });
  };

  updateInvolvedSubstances = (values: LegalPoliceOtherOffenseItemModel['InvolvedSubstances']) => {
    this.update({
      InvolvedSubstances: values,
    });
  };

  updateInvolvedAlcohol = (values: LegalPoliceOtherOffenseItemModel['InvolvedAlcohol']) => {
    this.update({
      InvolvedAlcohol: values,
    });
  };

  updateReasonNotCharged = (values: LegalPoliceOtherOffenseItemModel['ReasonNotCharged']) => {
    this.update({
      ReasonNotCharged: values,
    });
  };

  updateCourtName = (values: LegalPoliceOtherOffenseItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtAddress = (values: LegalPoliceOtherOffenseItemModel['CourtAddress']) => {
    this.update({
      CourtAddress: values,
    });
  };

  updateCharges = (values: LegalPoliceOtherOffenseItemModel['Charges']) => {
    this.update({
      Charges: values,
    });
  };

  updateWasSentenced = (values: LegalPoliceOtherOffenseItemModel['WasSentenced']) => {
    this.update({
      WasSentenced: values,
    });
  };

  updateSentence = (values: LegalPoliceOtherOffenseItemModel['Sentence']) => {
    this.update({
      Sentence: values,
    });
  };

  updateAwaitingTrial = (values: LegalPoliceOtherOffenseItemModel['AwaitingTrial']) => {
    this.update({
      AwaitingTrial: values,
    });
  };

  updateAwaitingTrialExplanation = (
    values: LegalPoliceOtherOffenseItemModel['AwaitingTrialExplanation']
  ) => {
    this.update({
      AwaitingTrialExplanation: values,
    });
  };

  summary = (item, index) => {
    const itemProperties = (item || {}).Item || {};
    const charge = itemProperties.CourtCharge && itemProperties.CourtCharge.value;
    const date = DateSummary(itemProperties.CourtDate);

    return Summary({
      type: i18n.t('legal.police.collection.summary.item'),
      index,
      left: charge,
      right: date,
      placeholder: i18n.t('legal.police.collection.summary.unknown'),
    });
  };

  render() {
    const {
      requireLegalPoliceDrugs, requireLegalPoliceFirearms, errors, formType
    } = this.props;
    const chargesErrors = errors && errors.filter((e) => e.indexOf('Charges.accordion') > -1);
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(formType);

    return isPVQ ? (
      <div className="offense">
        <Field
          title={i18n.t('legal.pvq.police.heading.dateOtherOffenseArrest')}
          help="legal.pvq.police.help.date"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            {...this.props.Date}
            name="Date"
            className="offense-date"
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('legal.pvq.police.heading.offenseArrestingAgency')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <Text
            {...this.props.CourtName}
            name="CourtName"
            className="offense-courtname"
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtName`}
          />
        </Field>

        <PVQLocation
          {...this.props.CourtAddress}
          name="CourtAddress"
          attribute={`${attribute}.CourtAddress`}
          label={i18n.t('legal.pvq.police.heading.offenseArrestingAgencyAddress')}
          domesticTitle={i18n.t('legal.pvq.police.label.agencyAddress')}
          foreignTitle={i18n.t('legal.pvq.police.label.agencyAddressForeign')}
          layout={Location.ADDRESS}
          geocode
          addressBook="Facility"
          addressBooks={this.props.addressBooks}
          dispatch={this.props.dispatch}
          onUpdate={this.updateCourtAddress}
          onError={this.props.onError}
          required={this.props.required}
          isPoBoxAllowed={false}
          showInternationalStreet={false}
        />

        <Field
          title={i18n.t('legal.pvq.police.heading.descriptionotheroffense')}
          help="legal.pvq.police.help.description"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            {...this.props.Description}
            name="Description"
            className="offense-description"
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>
        <Field
          title={i18n.t('legal.pvq.police.heading.dateOtherOffenseOccurred')}
          help="legal.pvq.police.help.date"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OffenseDate`}
        >
          <DateControl
            {...this.props.OffenseDate}
            name="OffenseDate"
            className="offense-date"
            minDateEqualTo
            onUpdate={this.updateOffenseDate}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.OffenseDate`}
          />
        </Field>
        <Field
          title={i18n.t('legal.pvq.police.heading.address')}
          attribute={`${attribute}.OffenseAddress`}
        >
          <PVQLocation
            {...this.props.OffenseAddress}
            name="OffenseAddress"
            attribute={`${attribute}.OffenseAddress`}
            label={i18n.t('legal.pvq.police.label.otherOffenseAddressUS')}
            countyLabel={i18n.t('legal.pvq.police.label.county')}
            layout={PVQLocation.US_COUNTY_CITY_STATE_ZIP_INTERNATIONAL_CITY_COUNTRY}
            geocode
            addressBook="Facility"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateOffenseAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            showInternationalStreet={false}
          />
        </Field>
        <Branch
          {...this.props.InvolvedViolence}
          name="involved_violence"
          label={i18n.t('legal.pvq.police.heading.involvement')}
          labelSize="h4"
          className="offense-violence no-margin-bottom"
          onUpdate={this.updateInvolvedViolence}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InvolvedViolence`}
        >
          {i18n.t('legal.pvq.police.label.violenceConcise')}
        </Branch>

        <Branch
          {...this.props.InvolvedFirearms}
          name="involved_firearms"
          className="offense-firearms no-margin-bottom"
          onUpdate={this.updateInvolvedFirearms}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InvolvedFirearms`}
        >
          {i18n.t('legal.pvq.police.label.firearms')}
        </Branch>
        <Branch
          {...this.props.InvolvedExplosives}
          name="involved_explosives"
          className="offense-firearms no-margin-bottom"
          onUpdate={this.updateInvolvedExplosives}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InvolvedExplosives`}
        >
          {i18n.t('legal.pvq.police.label.explosives')}
        </Branch>

        <Branch
          {...this.props.InvolvedSubstances}
          name="involved_substances"
          className="offense-substances no-margin-bottom"
          onUpdate={this.updateInvolvedSubstances}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InvolvedSubstances`}
        >
          {i18n.t('legal.pvq.police.label.substances')}
        </Branch>
        <Branch
          {...this.props.InvolvedAlcohol}
          name="involved_alcohol"
          className="offense-substances"
          onUpdate={this.updateInvolvedAlcohol}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InvolvedAlcohol`}
        >
          {i18n.t('legal.pvq.police.label.alcohol')}
        </Branch>
        <Field
          title={i18n.t('legal.pvq.police.heading.otherOffenseReasonNotCharged')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ReasonNotCharged`}
        >
          <Textarea
            {...this.props.ReasonNotCharged}
            name="ReasonNotCharged"
            className="offense-description"
            onUpdate={this.updateReasonNotCharged}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.ReasonNotCharged`}
          />
        </Field>
      </div>
    ) : (
      <div className="offense">
        <Field
          title={i18n.t('legal.police.heading.date')}
          help="legal.police.help.date"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            className="offense-date"
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('legal.police.heading.description')}
          help="legal.police.help.description"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            name="Description"
            {...this.props.Description}
            className="offense-description"
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        <Branch
          name="involved_violence"
          label={i18n.t('legal.police.heading.involvement')}
          labelSize="h4"
          className="offense-violence no-margin-bottom"
          {...this.props.InvolvedViolence}
          onUpdate={this.updateInvolvedViolence}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InvolvedViolence`}
        >
          {i18n.t('legal.police.label.violence')}
        </Branch>

        {
          requireLegalPoliceFirearms && (
            <Branch
              name="involved_firearms"
              className="offense-firearms no-margin-bottom"
              {...this.props.InvolvedFirearms}
              onUpdate={this.updateInvolvedFirearms}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedFirearms`}
            >
              {i18n.t('legal.police.label.firearms')}
            </Branch>
          )
        }

        {
          requireLegalPoliceDrugs && (
            <Branch
              name="involved_substances"
              className="offense-substances"
              {...this.props.InvolvedSubstances}
              onUpdate={this.updateInvolvedSubstances}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.InvolvedSubstances`}
            >
              {i18n.t('legal.police.label.substances')}
            </Branch>
          )
        }

        <Field
          title={i18n.t('legal.police.heading.courtname')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <Text
            name="CourtName"
            {...this.props.CourtName}
            label={i18n.t('legal.police.label.courtname')}
            className="offense-courtname"
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtName`}
          />
        </Field>

        <Field
          title={i18n.t('legal.police.heading.courtaddress')}
          optional
          help="legal.police.help.courtaddress"
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtAddress`}
        >
          <Location
            name="CourtAddress"
            {...this.props.CourtAddress}
            label={i18n.t('legal.police.label.address')}
            className="offense-courtaddress"
            layout={Location.OFFENSE}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Court"
            dispatch={this.props.dispatch}
            onUpdate={this.updateCourtAddress}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtAddress`}
          />
        </Field>

        <div style={{ marginBottom: '8rem' }}>
          <Field
            title={i18n.t('legal.police.heading.chargedetails')}
            titleSize="h4"
            className="no-margin-bottom"
          >
            {i18n.m('legal.police.para.chargedetails')}
          </Field>

          <Accordion
            className="offense-charges"
            {...this.props.Charges}
            defaultState={this.props.defaultState}
            onUpdate={this.updateCharges}
            onError={this.props.onError}
            summary={this.summary}
            description={i18n.t('legal.police.collection.summary.title')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            errors={chargesErrors}
            attribute={`${attribute}.Charges`}
            ignoreInitialScrollTo
          >
            <Charge
              name="Item"
              bind
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Charges`}
            />
          </Accordion>
        </div>

        <Branch
          name="was_sentenced"
          label={i18n.t('legal.police.heading.otherOffenseSentenced')}
          labelSize="h4"
          className="offense-sentenced"
          {...this.props.WasSentenced}
          onUpdate={this.updateWasSentenced}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.WasSentenced`}
        />

        <Show when={(this.props.WasSentenced || {}).value === 'Yes'}>
          <div>
            <Sentence
              name="Sentence"
              {...this.props.Sentence}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateSentence}
              scrollIntoView={this.props.scrollIntoView}
              requireLegalOffenseSentenced
              requireLegalOffenseIncarcerated
              attribute={`${attribute}.Sentence`}
            />
          </div>
        </Show>
        <Show when={(this.props.WasSentenced || {}).value === 'No'}>
          <div>
            <Branch
              name="awaiting_trial"
              label={i18n.t('legal.police.heading.awaitingTrial')}
              labelSize="h4"
              className="awaiting-trial no-margin-bottom"
              {...this.props.AwaitingTrial}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateAwaitingTrial}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.AwaitingTrial`}
            />
            <Field
              title={i18n.t('legal.police.heading.awaitingTrialExplanation')}
              titleSize="label"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.AwaitingTrialExplanation`}
            >
              <Textarea
                className="awaiting-trial-explanation"
                {...this.props.AwaitingTrialExplanation}
                name="awaiting_trial_explanation"
                onError={this.props.onError}
                required={this.props.required}
                onUpdate={this.updateAwaitingTrialExplanation}
                attribute={`${attribute}.AwaitingTrialExplanation`}
              />
            </Field>
          </div>
        </Show>
      </div>
    );
  }
}

OtherOffense.defaultProps = {
  InvolvedViolence: {},
  InvolvedFirearms: {},
  InvolvedSubstances: {},
  InvolvedExplosives: {}, // PVQ Only
  InvolvedAlcohol: {}, // PVQ Only
  ReasonNotCharged: {}, // PVQ Only
  OffenseDate: {}, // PVQ Only
  Charges: { items: [] },
  WasSentenced: {},
  addressBooks: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
  requireLegalPoliceDrugs: true,
  requireLegalPoliceFirearms: true,
};
